/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.scss'
import ValueCard from './ValueCard'
import { ReactComponent as AllIncomeIcon } from '../../../assets/icons/expense-icons/all-profit.svg'
import { ReactComponent as ArcIcon } from '../../../assets/icons/expense-icons/arc-up.svg'
import { ReactComponent as ArcDIcon } from '../../../assets/icons/expense-icons/arc-down.svg'
import { ReactComponent as ExpensesIcon } from '../../../assets/icons/expense-icons/shipments.svg'
import { ReactComponent as FoodIcon } from '../../../assets/icons/expense-icons/food.svg'
import { ReactComponent as MaterialIcon } from '../../../assets/icons/expense-icons/material.svg'
import { ReactComponent as PayingPatients } from '../../../assets/icons/expense-icons/patients-svg.svg'
import { ReactComponent as CachierIcon } from '../../../assets/icons/expense-icons/cachier.svg'
import { ReactComponent as PulmonologyIcon } from '../../../assets/icons/pulmonology.svg'
import { ReactComponent as StomatologyIcon } from '../../../assets/icons/stomatology.svg'
import { togglePopup } from '../../../Redux/popups/popupSlice'
import DualLineCharts from './DualLineCharts'
import ChartControl from './ChartControl'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { addNewWithdraw } from '../../../Redux/withdraws/withdrawsSlice'
import printProfitPDF from '../../../utils/printProfitPDF'

const CARRY_OVER_DESC = 'Kaluar nga dita e mëparshme'
const RETRO_CARRY_OVER_DESC = 'Kaluar nga dita e mëparshme (retroaktiv)'

function PaymentsReviewComponent() {
  const dispatch = useDispatch()
  const { t } = useTranslation() || ((input) => input)
  const auth = useSelector((state) => state.auth)
  const { profile } = auth?.user || {}

  // CENTRALIZED Helper: format any date-like input as YYYY-MM-DD in Europe/Tirane
  const formatDateTirane = useCallback((dateInput) => {
    if (dateInput == null) return null

    if (typeof dateInput === 'string') {
      const trimmed = dateInput.trim()
      if (!trimmed) return null
      const ymd = trimmed.slice(0, 10)
      if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        return ymd
      }
    }

    const dateObj = typeof dateInput?.toDate === 'function'
      ? dateInput.toDate()
      : dateInput instanceof Date
        ? dateInput
        : new Date(dateInput)

    if (!dateObj || isNaN(dateObj.getTime())) {
      console.warn('Invalid date passed to formatDateTirane:', dateInput)
      return null
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Tirane',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(dateObj)
    const get = (type) => parts.find((p) => p.type === type)?.value ?? ''
    return `${get('year')}-${get('month')}-${get('day')}`
  }, [])

  // Backward compatible alias used in this component
  const formatDateSafely = formatDateTirane

  // Always derive next working day from Tirane calendar date (skip Sunday).
  const getNextWorkingDayTirane = useCallback((baseDateObj = new Date()) => {
    const baseDate = formatDateSafely(baseDateObj)
    if (!baseDate) return null

    const [year, month, day] = baseDate.split('-').map(Number)
    const next = new Date(Date.UTC(year, month - 1, day + 1))
    while (next.getUTCDay() === 0) {
      next.setUTCDate(next.getUTCDate() + 1)
    }
    return formatDateSafely(next)
  }, [formatDateSafely])

  const isCarryOverDescription = useCallback((description) => {
    return description === CARRY_OVER_DESC || description === RETRO_CARRY_OVER_DESC
  }, [])

  // Remove admin check and redirect
  const [dateRangeType, setDateRangeType] = useState('day')
  const [dateRanges, setDateRanges] = useState(() => {
    return [formatDateSafely(new Date())]
  })
  const { appointments } = useSelector((state) => state.appointments)
  const { expenses } = useSelector((state) => state.expenses)
  const { withdraws, loading: withdrawsLoading } = useSelector((state) => state.withdraws)
  const [showWarning, setShowWarning] = useState(false)
  const { patients } = useSelector((state) => state.patients)
  
  // Simple delay before showing warning to avoid false alarms during loading
  useEffect(() => {
    if (withdraws && withdraws.length === 0 && !withdrawsLoading) {
      const timer = setTimeout(() => {
        setShowWarning(true)
      }, 2000) // Wait 2 seconds before showing warning
      
      return () => clearTimeout(timer)
    } else {
      setShowWarning(false)
    }
  }, [withdraws, withdrawsLoading])
  const [selectedType, setSelectedType] = useState('all')
  const [showChartExpenses, setShowChartExpenses] = useState(true)
  const [showChartIncome, setShowChartIncome] = useState(true)
  
  // UNIFIED STATE: Track if ANY transfer has been completed today (manual OR automatic)
  const [anyTransferCompleted, setAnyTransferCompleted] = useState(() => {
    const today = formatDateSafely(new Date())
    const savedState = localStorage.getItem('anyTransferCompleted')
    const savedDate = localStorage.getItem('anyTransferDate')
    
    // Nëse data e ruajtur është e ndryshme nga sot, rivendos gjendjen
    if (savedDate !== today) {
      localStorage.removeItem('anyTransferCompleted')
      localStorage.removeItem('anyTransferDate')
      localStorage.removeItem('transferButtonClicked') // Clean up old states
      localStorage.removeItem('transferButtonDate')
      localStorage.removeItem('autoTransferCompleted')
      localStorage.removeItem('autoTransferDate')
      return false
    }
    
    // Check if any old transfer states exist and migrate them
    const oldManualState = localStorage.getItem('transferButtonClicked')
    const oldAutoState = localStorage.getItem('autoTransferCompleted')
    
    if (oldManualState === 'true' || oldAutoState === 'true') {
      return true
    }
    
    return savedState === 'true'
  })

  // LEGACY STATES: Keep for compatibility but derive from unified state
  const transferButtonClicked = anyTransferCompleted
  const autoTransferCompleted = anyTransferCompleted

  // Ref për të parandaluar transferime të shumta në të njëjtën kohë
  const transferInProgress = useRef(false)

  // 1. Make showArkaButtons true so the transfer button is always visible for admins
  const showArkaButtons = profile?.role !== 'standard';

  // Shto state për muaj dhe vit
  const [selectedMonth, setSelectedMonth] = useState(() => (new Date().getMonth() + 1).toString().padStart(2, '0'))
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear().toString())

  // Gjenero listën e viteve (p.sh. 2022-2030)
  const years = []
  for (let y = 2022; y <= new Date().getFullYear() + 2; y++) {
    years.push(y.toString())
  }

  // Memoized helper function to check if a patient exists
  const patientExists = useCallback((patientId) => {
    return patients?.some(patient => patient.id === patientId)
  }, [patients])

  // Add useEffect to handle standard user restrictions
  useEffect(() => {
    if (profile?.role === 'standard') {
      setDateRangeType('day')
      const today = formatDateSafely(new Date())
      setDateRanges([today])
    }
  }, [profile?.role])



  // UNIFIED STATE MANAGEMENT: Save any transfer completion to localStorage
  useEffect(() => {
    const today = formatDateSafely(new Date())
    if (anyTransferCompleted) {
      localStorage.setItem('anyTransferCompleted', 'true')
      localStorage.setItem('anyTransferDate', today)
      // Clean up old individual states
      localStorage.removeItem('transferButtonClicked')
      localStorage.removeItem('transferButtonDate')
      localStorage.removeItem('autoTransferCompleted')
      localStorage.removeItem('autoTransferDate')
    } else {
      localStorage.removeItem('anyTransferCompleted')
      localStorage.removeItem('anyTransferDate')
    }
  }, [anyTransferCompleted])

  // Monitoroj ndryshimet në withdraws për të parë nëse transferi është fshirë
  useEffect(() => {
    const nextWorkingDay = getNextWorkingDayTirane(new Date());
    if (!nextWorkingDay) return;
    
    // Kontrollo nëse transferi për ditën e ardhshme ekziston
    const transferExists = withdraws?.some(withdraw => 
      formatDateSafely(withdraw?.date) === nextWorkingDay && 
      isCarryOverDescription(withdraw.description)
    )
    
    // Nëse transferi ishte bërë por nuk ekziston më në databazë, riaktivizo mundësinë e transferimit
    if (anyTransferCompleted && !transferExists) {
      setAnyTransferCompleted(false)
    }
  }, [withdraws, anyTransferCompleted, getNextWorkingDayTirane, formatDateSafely, isCarryOverDescription])

  // Kontrollo nëse ka të dhëna të fshira dhe jep paralajmërim
  useEffect(() => {
    if (withdraws && withdraws.length === 0) {
      // Silent check for missing data
    }
  }, [withdraws])

  // Kur ndryshon muaji ose viti dhe është monthly, rifresko datat
  useEffect(() => {
    if (dateRangeType === 'monthly') {
      const year = parseInt(selectedYear)
      const month = parseInt(selectedMonth) - 1
      const startOfMonth = new Date(year, month, 1)
      const endOfMonth = new Date(year, month + 1, 0)
      const days = []
      const currentDate = new Date(startOfMonth)
      while (currentDate <= endOfMonth) {
        const formattedDate = formatDateSafely(currentDate)
        if (formattedDate) days.push(formattedDate)
        currentDate.setDate(currentDate.getDate() + 1)
      }
      setDateRanges(days)
    }
  }, [selectedMonth, selectedYear, dateRangeType])

  // Kur kalon në monthly, vendos muajin/vitin aktual
  useEffect(() => {
    if (dateRangeType === 'monthly') {
      setSelectedMonth((new Date().getMonth() + 1).toString().padStart(2, '0'))
      setSelectedYear(new Date().getFullYear().toString())
    }
  }, [dateRangeType])

  const switchRange = useCallback((type) => {
    // Prevent standard users from changing date range
    if (profile?.role === 'standard') return
    
    setDateRangeType(type)
    const date = new Date()
    const today = formatDateSafely(date)
    switch (type) {
      case 'day':
        setDateRanges([today])
        break
      case 'weekly': {
        const thisMonday = new Date(date)
        thisMonday.setDate(date.getDate() - date.getDay() + 1)
        const weekly = [formatDateSafely(thisMonday)]
        for (let i = 0; i < 6; i++) {
          const nextDate = new Date(thisMonday)
          nextDate.setDate(thisMonday.getDate() + i + 1)
          weekly.push(formatDateSafely(nextDate))
        }
        setDateRanges(weekly)
        break
      }
      case 'monthly': {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0)
        const days = []
        const currentDate = new Date(startOfMonth)
        
        // Use centralized date formatting function
        while (currentDate <= endOfMonth) {
          const formattedDate = formatDateSafely(currentDate)
          if (formattedDate) days.push(formattedDate)
          currentDate.setDate(currentDate.getDate() + 1)
        }
        setDateRanges(days)
        break
      }
      case 'all':
        setDateRanges(null)
        break
    }
  }, [profile?.role])

  // Memoize expensive filtering and calculations
  const categorizedApointments = useMemo(() => appointments?.filter((appt) => {
    // First check if patient exists
    if (!patientExists(appt.patient_id)) return false;
    
    // Then filter by category
    const categoryMatch = selectedType === 'all' || 
      (!appt?.category && selectedType === 'stomatology') || 
      appt?.category === selectedType;

    // Then filter by date range
    if (!categoryMatch) return false;
    
    if (!Array.isArray(dateRanges) || dateRangeType === 'all') return true;
    
    try {
      // First check if there are payments in array_of_payments within the date range
      if (Array.isArray(appt.array_of_payments) && appt.array_of_payments.length > 0) {
        const hasPaymentInRange = appt.array_of_payments.some(payment => {
          if (!payment.date) return false;
          try {
            const formattedDate = formatDateSafely(payment.date);
            return dateRanges.some(range => formattedDate === range);
          } catch {
            return false;
          }
        });
        
        if (hasPaymentInRange) return true;
      }
      
      // Fallback to payment_date or appointment date for legacy appointments
      const dateToCheck = appt.payment_date || appt.date;
      if (!dateToCheck) return false;

      const formattedDate = formatDateSafely(dateToCheck);
      return formattedDate && dateRanges.some(range => formattedDate === range);
    } catch (error) {
      console.error('Error processing appointment date:', error);
      return false;
    }
  }), [appointments, patients, selectedType, dateRanges, dateRangeType])

  // Simplified payment calculation function

  // Simplified payment calculation function
  const calculatePaymentAmount = useCallback((appointment) => {
    if (!appointment) return 0
    
    // First try to get payment from array_of_payments
    if (Array.isArray(appointment.array_of_payments) && appointment.array_of_payments.length > 0) {
      return appointment.array_of_payments.reduce((sum, payment) => {
        const amount = Number(payment.amount) || Number(payment.value) || 0
        return sum + (isNaN(amount) ? 0 : amount)
      }, 0)
    }
    
    // Fallback to legacy payment fields
    const legacyAmount = Number(appointment.patient_pay) || Number(appointment.paid_amount) || 0
    return isNaN(legacyAmount) ? 0 : legacyAmount
  }, [])

  // Simplified date range filtering
  const isDateInRange = useCallback((dateToCheck, ranges, rangeType) => {
    if (!dateToCheck) return false
    if (rangeType === 'all') return true
    if (!Array.isArray(ranges) || ranges.length === 0) return false
    
    try {
      const formattedDate = formatDateSafely(dateToCheck)
      if (!formattedDate) return false
      
      return ranges.some(range => formattedDate === range)
    } catch {
      return false
    }
  }, [formatDateSafely])

  // Check if appointment has payments in date range
  const hasPaymentsInRange = useCallback((appointment, ranges, rangeType) => {
    if (rangeType === 'all') return true
    if (!Array.isArray(ranges) || ranges.length === 0) return false
    
    // Check array_of_payments first
    if (Array.isArray(appointment.array_of_payments) && appointment.array_of_payments.length > 0) {
      return appointment.array_of_payments.some(payment => 
        isDateInRange(payment.date, ranges, rangeType)
      )
    }
    
    // Fallback to appointment date
    return isDateInRange(appointment.payment_date || appointment.date, ranges, rangeType)
  }, [isDateInRange])

  // Get payment amount for specific date range
  const getPaymentForDateRange = useCallback((appointment, ranges, rangeType) => {
    if (!appointment) return 0
    if (rangeType === 'all') {
      return calculatePaymentAmount(appointment)
    }
    if (!Array.isArray(ranges) || ranges.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('getPaymentForDateRange called without valid ranges', {
          appointmentId: appointment?.id,
          rangeType
        })
      }
      return 0
    }
    
    // If appointment has array_of_payments, filter by date range
    if (Array.isArray(appointment.array_of_payments) && appointment.array_of_payments.length > 0) {
      const paymentsInRange = appointment.array_of_payments.filter(payment => 
        isDateInRange(payment.date, ranges, rangeType)
      )
      return paymentsInRange.reduce((sum, payment) => {
        const amount = Number(payment.amount) || Number(payment.value) || 0
        return sum + (isNaN(amount) ? 0 : amount)
      }, 0)
    }
    
    // For legacy appointments, check if the appointment date is in range
    if (isDateInRange(appointment.payment_date || appointment.date, ranges, rangeType)) {
      return calculatePaymentAmount(appointment)
    }
    
    return 0
  }, [calculatePaymentAmount, isDateInRange])

  // Memoized function for getting filtered payments by date range
  const getPaymentForDateRangeMemoized = useCallback((apt) => {
    return getPaymentForDateRange(apt, dateRanges, dateRangeType)
  }, [getPaymentForDateRange, dateRanges, dateRangeType]);

  // Funksion i veçantë për arkën e sotme që merr pagesat e sotit
  const getTodayPayment = useCallback((apt) => {
    const today = formatDateSafely(new Date());
    if (!today) return 0;
    
    // Kontrollojmë array_of_payments për pagesat e sotme
    if (Array.isArray(apt.array_of_payments) && apt.array_of_payments.length > 0) {
      const todayPayments = apt.array_of_payments.filter(payment => {
        if (!payment.date) return false;
        try {
          const formattedDate = formatDateSafely(payment.date);
          return formattedDate === today;
        } catch {
          return false;
        }
      });
      
      return todayPayments.reduce((sum, payment) => {
        const amount = Number(payment.amount) || Number(payment.value) || 0
        return sum + (isNaN(amount) ? 0 : amount)
      }, 0);
    }
    
    // Fallback për payment_date tradicionale
    try {
      const dateToCheck = apt.payment_date || apt.date;
      if (!dateToCheck) return 0;
      const formattedDate = formatDateSafely(dateToCheck);
      if (formattedDate === today) {
        return calculatePaymentAmount(apt);
      }
    } catch {
      return 0;
    }
    
    return 0;
  }, [formatDateSafely, calculatePaymentAmount]);

  // Memoize expensive total calculations
  const allTotalPatientPay = useMemo(() => (appointments?.filter((appt) => {
    if (!patientExists(appt.patient_id)) return false;
    if (!Array.isArray(dateRanges) || dateRangeType === 'all') return true;
    try {
      // First check if there are payments in array_of_payments within the date range
      if (Array.isArray(appt.array_of_payments) && appt.array_of_payments.length > 0) {
        const hasPaymentInRange = appt.array_of_payments.some(payment => {
          if (!payment.date) return false;
          try {
            const formattedDate = formatDateSafely(payment.date);
            return dateRanges.some(range => formattedDate === range);
          } catch {
            return false;
          }
        });
        
        if (hasPaymentInRange) return true;
      }
      
      // Fallback to payment_date or appointment date for legacy appointments
      const dateToCheck = appt.payment_date || appt.date;
      if (!dateToCheck) return false;
      const formattedDate = formatDateSafely(dateToCheck);
      return formattedDate && dateRanges.some(range => formattedDate === range);
    } catch {
      return false;
    }
  })?.reduce((sum, apt) => sum + getPaymentForDateRange(apt, dateRanges, dateRangeType), 0) || 0), [appointments, patients, dateRanges, dateRangeType, getPaymentForDateRange]);
  const allTotalPatientPayFixed = Number(allTotalPatientPay).toFixed(2);

  // Llogarit totalin për pulmonologji
  const pulmonologyPatientPay = useMemo(() => (appointments?.filter((appt) => {
    if (!patientExists(appt.patient_id)) return false;
    if (appt?.category !== 'pulmonology') return false;
    if (!Array.isArray(dateRanges) || dateRangeType === 'all') return true;
    try {
      // First check if there are payments in array_of_payments within the date range
      if (Array.isArray(appt.array_of_payments) && appt.array_of_payments.length > 0) {
        const hasPaymentInRange = appt.array_of_payments.some(payment => {
          if (!payment.date) return false;
          try {
            const formattedDate = formatDateSafely(payment.date);
            return dateRanges.some(range => formattedDate === range);
          } catch {
            return false;
          }
        });
        
        if (hasPaymentInRange) return true;
      }
      
      // Fallback to payment_date or appointment date for legacy appointments
      const dateToCheck = appt.payment_date || appt.date;
      if (!dateToCheck) return false;
      const formattedDate = formatDateSafely(dateToCheck);
      return formattedDate && dateRanges.some(range => formattedDate === range);
    } catch {
      return false;
    }
  })?.reduce((sum, apt) => sum + getPaymentForDateRange(apt, dateRanges, dateRangeType), 0) || 0), [appointments, patients, dateRanges, dateRangeType, getPaymentForDateRange]);
  const pulmonologyPatientPayFixed = Number(pulmonologyPatientPay).toFixed(2);

  // Ndrysho llogaritjen e totalPatientPay për stomatologji
  const totalPatientPay = selectedType === 'stomatology'
    ? (Number(allTotalPatientPayFixed) - Number(pulmonologyPatientPayFixed)).toFixed(2)
    : (categorizedApointments?.reduce((sum, apt) => sum + getPaymentForDateRange(apt, dateRanges, dateRangeType), 0) || 0).toFixed(2);

  // Calculate total service costs
  const totalServiceCost = useMemo(() => 
    (categorizedApointments?.reduce((sum, apt) => 
      sum + (Number(apt.service_cost) || Number(apt.price) || 0), 0) || 0).toFixed(2), 
    [categorizedApointments]);

  // Calculate total difference
  const totalDifference = (categorizedApointments?.reduce((sum, apt) => {
    const serviceCost = Number(apt.service_cost) || Number(apt.price) || 0;
    const patientPay = Number(apt.patient_pay) || Number(apt.paid_amount) || 0;
    return sum + (serviceCost - patientPay);
  }, 0) || 0).toFixed(2);

  // Ndrysho llogaritjen e payingPatientsSorted për stomatologji
  const payingPatientsSorted = selectedType === 'stomatology'
    ? appointments
        ?.filter((appointment) => {
          if (!patientExists(appointment.patient_id)) return false;
          if (appointment?.category === 'pulmonology') return false;
          if (!Array.isArray(dateRanges) || dateRangeType === 'all') return getPaymentForDateRange(appointment, dateRanges, dateRangeType) > 0 && appointment.patient_id;
          try {
            // First check if there are payments in array_of_payments within the date range
            if (Array.isArray(appointment.array_of_payments) && appointment.array_of_payments.length > 0) {
              const hasPaymentInRange = appointment.array_of_payments.some(payment => {
                if (!payment.date) return false;
                try {
                  const formattedDate = formatDateSafely(payment.date);
                  return dateRanges.some(range => formattedDate === range);
                } catch {
                  return false;
                }
              });
              
              if (hasPaymentInRange) return getPaymentForDateRange(appointment, dateRanges, dateRangeType) > 0 && appointment.patient_id;
            }
            
            // Fallback to payment_date or appointment date for legacy appointments
            const dateToCheck = appointment.payment_date || appointment.date;
            if (!dateToCheck) return false;
            const formattedDate = formatDateSafely(dateToCheck);
            return dateRanges.some(range => formattedDate === range) && getPaymentForDateRange(appointment, dateRanges, dateRangeType) > 0 && appointment.patient_id;
          } catch {
            return false;
          }
        })
        ?.reduce((acc, appointment) => {
          const existingPatient = acc.find(p => p.patient_id === appointment.patient_id);
          if (existingPatient) {
            existingPatient.patient_pay += getPaymentForDateRange(appointment, dateRanges, dateRangeType);
          } else {
            acc.push({ ...appointment, patient_pay: getPaymentForDateRange(appointment, dateRanges, dateRangeType) });
          }
          return acc;
        }, [])
        ?.sort((a, b) => a.patient_pay - b.patient_pay)
    : categorizedApointments
        ?.filter((appointment) => getPaymentForDateRange(appointment, dateRanges, dateRangeType) > 0 && appointment.patient_id)
        ?.reduce((acc, appointment) => {
          const existingPatient = acc.find(p => p.patient_id === appointment.patient_id);
          if (existingPatient) {
            existingPatient.patient_pay += getPaymentForDateRange(appointment, dateRanges, dateRangeType);
          } else {
            acc.push({ ...appointment, patient_pay: getPaymentForDateRange(appointment, dateRanges, dateRangeType) });
          }
          return acc;
        }, [])
        ?.sort((a, b) => a.patient_pay - b.patient_pay)

  // Memoize expenses calculations
  const rangedExpenses = useMemo(() => 
    Array.isArray(dateRanges) && dateRangeType !== 'all'
      ? expenses?.filter((expense) => {
          const formattedDate = formatDateSafely(expense?.date);
          return formattedDate && dateRanges?.some((range) => formattedDate === range);
        }) ?? []
      : expenses, 
    [expenses, dateRanges, dateRangeType])

  const allExpenses = useMemo(() => 
    (rangedExpenses?.reduce((acc, expense) => 
      acc + (Number(expense?.paid_amount) || 0), 0) || 0).toFixed(2), 
    [rangedExpenses]);

  const foodExpenses = useMemo(() => 
    (rangedExpenses
      ?.filter((expense) => expense.category === 'Food')
      ?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0).toFixed(2), 
    [rangedExpenses]);

  const materialExpenses = useMemo(() => 
    (rangedExpenses
      ?.filter((expense) => expense.category === 'Material')
      ?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0).toFixed(2), 
    [rangedExpenses]);

  const techExpenses = useMemo(() => 
    (rangedExpenses
      ?.filter((expense) => expense.category === 'Tech')
      ?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0).toFixed(2), 
    [rangedExpenses]);

  const otherExpenses = useMemo(() => 
    (rangedExpenses
      ?.filter((expense) => expense.category === 'Other')
      ?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0).toFixed(2), 
    [rangedExpenses]);

  // Calculate total profit
  const totalProfit = (Number(totalPatientPay) - Number(allExpenses)).toFixed(2);

  // Calculate current cashier balance
  // Note: Arka përdor totalin e të gjitha kategorive (allTotalPatientPayFixed) 
  // sepse shpenzimet janë të përgjithshme dhe nuk mund të ndahen sipas kategorive
  
  // Cashier balance always uses a single day range: selected day (in day view) or today.
  const today = formatDateSafely(new Date());
  const cashierSelectedDate = useMemo(() => {
    if (dateRangeType === 'day' && Array.isArray(dateRanges) && dateRanges.length === 1) {
      return dateRanges[0];
    }
    return today;
  }, [dateRangeType, dateRanges, today]);

  const cashierDayRange = useMemo(() => (
    cashierSelectedDate ? [cashierSelectedDate] : []
  ), [cashierSelectedDate]);

  // Include all appointments for the day (do not filter by patientExists – ledger is immutable)
  const todayPatientPay = useMemo(() =>
    (appointments?.reduce((sum, apt) =>
      sum + getPaymentForDateRange(apt, cashierDayRange, 'day'), 0) || 0),
  [appointments, getPaymentForDateRange, cashierDayRange]);
  
  const todayExpenses = useMemo(() =>
    (expenses?.filter((expense) => {
      const formattedDate = formatDateSafely(expense?.date);
      return formattedDate === cashierSelectedDate;
    })?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0),
  [expenses, cashierSelectedDate, formatDateSafely]);
  
  const todayWithdraws = useMemo(() =>
    (withdraws?.filter((withdraw) => {
      const formattedDate = formatDateSafely(withdraw?.date);
      return formattedDate === cashierSelectedDate;
    }) ?? []),
  [withdraws, cashierSelectedDate, formatDateSafely]);
  
  const { todayAllWithdraws, todayAllInputs } = todayWithdraws.reduce(
    (acc, withdraw) => {
      if (withdraw.type === 'withdraw') {
        acc.todayAllWithdraws += Number(withdraw.amount) || 0
      } else {
        acc.todayAllInputs += Number(withdraw.amount) || 0
      }
      return acc
    },
    { todayAllWithdraws: 0, todayAllInputs: 0 }
  );

  // Llogarit arkan e mëparshme që duhet të përfshihet
  const getPreviousArka = () => {
    // Kontrollo nëse ka transferim nga dita e mëparshme për sot
    const previousTransfer = withdraws?.find(withdraw => 
      formatDateSafely(withdraw?.date) === today && 
      isCarryOverDescription(withdraw.description)
    );
    
    if (previousTransfer) {
      return Number(previousTransfer.amount);
    }
    
    // Nëse nuk ka transferim, llogarit arkan e dje
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDate = formatDateSafely(yesterday);
    
    // Llogarit pagesat e dje
    const yesterdayPatientPay = (appointments?.filter((appt) => {
      try {
        if (Array.isArray(appt.array_of_payments) && appt.array_of_payments.length > 0) {
          const hasYesterdayPayment = appt.array_of_payments.some(payment => {
            if (!payment.date) return false;
            try {
              const formattedDate = formatDateSafely(payment.date);
              return formattedDate === yesterdayDate;
            } catch {
              return false;
            }
          });
          
          if (hasYesterdayPayment) return true;
        }
        
        const dateToCheck = appt.payment_date || appt.date;
        if (!dateToCheck) return false;
        const formattedDate = formatDateSafely(dateToCheck);
        return formattedDate === yesterdayDate;
      } catch {
        return false;
      }
    })?.reduce((sum, apt) => sum + getPaymentForDateRange(apt, [yesterdayDate], 'day'), 0) || 0);
    
    // Llogarit shpenzimet e dje
    const yesterdayExpenses = (expenses?.filter((expense) => {
      try {
        const formattedDate = formatDateSafely(expense?.date);
        return formattedDate === yesterdayDate;
      } catch (error) {
        return false;
      }
    })?.reduce((acc, expense) => acc + (Number(expense?.paid_amount) || 0), 0) || 0);
    
    // Llogarit withdraws/inputs e dje
    const yesterdayWithdraws = (withdraws?.filter((withdraw) => {
      try {
        const formattedDate = formatDateSafely(withdraw?.date);
        return formattedDate === yesterdayDate;
      } catch (error) {
        return false;
      }
    }) ?? []);
    
    const { yesterdayAllWithdraws, yesterdayAllInputs } = yesterdayWithdraws.reduce(
      (acc, withdraw) => {
        if (withdraw.type === 'withdraw') {
          acc.yesterdayAllWithdraws += Number(withdraw.amount) || 0
        } else {
          acc.yesterdayAllInputs += Number(withdraw.amount) || 0
        }
        return acc
      },
      { yesterdayAllWithdraws: 0, yesterdayAllInputs: 0 }
    );
    
    // Llogarit arkan e dje
    const yesterdayArka = Number(yesterdayPatientPay) - Number(yesterdayExpenses) + Number(yesterdayAllInputs) - Number(yesterdayAllWithdraws);
    
    // Nëse arka e dje është shumë negative, mund të jetë gabim
    if (yesterdayArka < -500) {
      return 0;
    }
    
    return yesterdayArka;
  };

  // previousArka is no longer needed for currentlyInCachier calculation since transfers are handled in todayAllInputs
  // const previousArka = getPreviousArka();
  
  // Calculate morning cash register value (value that was actually transferred from previous day)
  const morningCashRegister = useMemo(() => {
    // Get the selected date (if viewing a specific date) or today
    const selectedDate = dateRanges && dateRanges.length === 1 ? dateRanges[0] : today;
    
    // Check if there's a transfer from previous day for the selected date
    const previousTransfer = withdraws?.find(withdraw => 
      formatDateSafely(withdraw?.date) === selectedDate && 
      isCarryOverDescription(withdraw.description)
    );
    
    if (previousTransfer) {
      return Number(previousTransfer.amount).toFixed(2);
    }
    
    // If no transfer exists, show 0
    return '0.00';
  }, [withdraws, dateRanges, today]);
  
  // Memoize the currentlyInCachier calculation (no cap: show true balance for auditing)
  const currentlyInCachier = useMemo(() => {
    const calculatedArka = Number(todayPatientPay) - Number(todayExpenses) + Number(todayAllInputs) - Number(todayAllWithdraws);
    return calculatedArka.toFixed(2);
  }, [todayPatientPay, todayExpenses, todayAllInputs, todayAllWithdraws]);

  // Variablat për rangedWithdraws që përdoren në pjesë të tjera të kodit
  const rangedWithdraws = Array.isArray(dateRanges) && dateRangeType !== 'all'
    ? withdraws?.filter((withdraw) => {
        const formattedDate = formatDateSafely(withdraw?.date);
        return formattedDate && dateRanges?.includes(formattedDate);
      }) ?? []
    : withdraws

  const { allWithdraws, allInputs } = (rangedWithdraws ?? []).reduce(
    (acc, withdraw) => {
      if (withdraw.type === 'withdraw') {
        acc.allWithdraws += Number(withdraw.amount) || 0
      } else {
        // Exclude day carry-over rows from generic "inputs" summary.
        if (!isCarryOverDescription(withdraw.description)) {
          acc.allInputs += Number(withdraw.amount) || 0
        }
      }
      return acc
    },
    { allWithdraws: 0, allInputs: 0 }
  )

  // Helper to check if selected date(s) are in the future
  const isFutureDateSelected = () => {
    if (!dateRanges || dateRanges.length === 0) return false;
    // Use the last date in the range (for monthly, weekly, etc.)
    const lastDate = dateRanges[dateRanges.length - 1];
    const today = new Date();
    const todayStr = formatDateSafely(today);
    return todayStr && lastDate > todayStr;
  };

  // Helper function për të llogaritur datën e sotme (must be before getArkaForDate)
  const getTodayDate = () => {
    const today = new Date();
    return formatDateSafely(today);
  };

  // Helper to get arka value for a given date. Uses one formula for all dates so the
  // morning carry-over (Kaluar nga dita e mëparshme) is only counted once as an input.
  const getArkaForDate = (dateStr) => {
    const today = getTodayDate();
    if (dateStr === today) return currentlyInCachier;

    // 1. Sum patient payments for that specific date
    const patientPay = (appointments?.filter((appt) => {
      if (Array.isArray(appt.array_of_payments) && appt.array_of_payments.length > 0) {
        return appt.array_of_payments.some(p => {
          if (!p?.date) return false;
          return formatDateSafely(p.date) === dateStr;
        });
      }
      const dateToCheck = appt.payment_date || appt.date;
      if (!dateToCheck) return false;
      return formatDateSafely(dateToCheck) === dateStr;
    }).reduce((sum, apt) => sum + getPaymentForDateRange(apt, [dateStr], 'day'), 0)) || 0;

    // 2. Sum expenses for that specific date
    const expensesForDay = (expenses?.filter((exp) => {
      return formatDateSafely(exp?.date) === dateStr;
    }).reduce((acc, exp) => acc + (Number(exp.paid_amount) || 0), 0)) || 0;

    // 3. Sum inputs and withdraws for that date (carry-over is already an input – count once)
    const withdrawsForDay = withdraws?.filter((w) => {
      return formatDateSafely(w?.date) === dateStr;
    }) || [];
    let dayInputs = 0;
    let dayWithdraws = 0;
    withdrawsForDay.forEach(w => {
      if (w.type === 'withdraw') dayWithdraws += Number(w.amount) || 0;
      else dayInputs += Number(w.amount) || 0;
    });

    // 4. Single formula: no manual previousArka – it's already in dayInputs
    const total = Number(patientPay) - Number(expensesForDay) + Number(dayInputs) - Number(dayWithdraws);
    return total.toFixed(2);
  };

  // For single-day view: show profit as change in register (Gjendja - Arka), so it matches the cash story
  const displayTotalProfit = (dateRanges?.length === 1 && dateRangeType === 'day')
    ? (Number(getArkaForDate(dateRanges[0])) - Number(morningCashRegister)).toFixed(2)
    : totalProfit;

  const handleAddingNewExpenseForm = useCallback(() => {
    dispatch(
      togglePopup({
        pointer: 7,
        data: { editing: false, exp: null }
      })
    )
  }, [dispatch])

  const handleAddingNewWithdrawForm = useCallback(() => {
    dispatch(
      togglePopup({
        pointer: 9
      })
    )
  }, [dispatch])

  const showPayingPatients = useCallback(() => {
    dispatch(
      togglePopup({
        pointer: 8
      })
    )
  }, [dispatch])

  const showArkaChanges = useCallback(() => {
    dispatch(
      togglePopup({
        pointer: 13,
        data: { type: 'arka' }
      })
    )
  }, [dispatch])


  const showExpenseChanges = useCallback(() => {
    dispatch(
      togglePopup({
        pointer: 12,
        data: { type: 'expense' }
      })
    )
  }, [dispatch])

  const printrecord = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    const overViewDate = dateRangeType === 'all' 
      ? 'Full' 
      : dateRanges?.length > 1 
        ? formatDate(dateRanges[0]) + ' - ' + formatDate(dateRanges[dateRanges.length - 1])
        : formatDate(dateRanges[0])

    // Merge patient data with appointment data
    const patientsWithInfo = payingPatientsSorted
      ?.map(appointment => {
        const patient = patients?.find(p => p.id === appointment.patient_id);
        return {
          ...appointment,
          full_name: patient?.full_name || '-',
          dob: patient?.dob || null,
          location: patient?.location || '-',
          category: patient?.category || 'stomatology'
        };
      }) || [];

    const data = {
      overViewDate,
      totalServiceCost,
      totalPatientPay,
      totalDifference,
      totalProfit: dateRangeType === 'day' && dateRanges?.length === 1
        ? (Number(getArkaForDate(dateRanges[0])) - Number(morningCashRegister)).toFixed(2)
        : totalProfit,
      allInputs,
      allWithdraws,
      currentlyInCachier,
      arkaMengjesit: dateRangeType === 'day' ? Number(morningCashRegister) || 0 : 0,
      expenses: {
        allExpenses,
        foodExpenses,
        materialExpenses,
        techExpenses,
        otherExpenses,
        rangedExpenses
      },
      selectedCategory: selectedType,
      patients: dateRangeType === 'day' ? patientsWithInfo : [],
      isDayFilter: dateRangeType === 'day'
    }
    localStorage.setItem('currentprintprofitData', JSON.stringify(data))
    printProfitPDF(data)
  }

  // Funksion për të kaluar arkan në ditën e nesërme
  const carryOverArka = () => {
    // ENHANCED CHECK: Ensure no transfer of any type has been completed today
    const hasActiveTransfer = checkIfArkaCarriedOver() || anyTransferCompleted || transferInProgress.current;
    
    // Mos lejo transferimin nëse ndonjë lloj transferi është bërë tashmë sot
    if (hasActiveTransfer) {
      return
    }
    
    // TRIPLE LOCK: Final check before execution
    if (transferInProgress.current || anyTransferCompleted || checkIfArkaCarriedOver()) {
      return; // Exit if any condition is already true
    }
    
    // IMMEDIATELY lock to prevent multiple executions
    transferInProgress.current = true;
    
    const todayDate = getTodayDate()
    const transferDate = getNextWorkingDayTirane(new Date());
    if (!transferDate) {
      transferInProgress.current = false;
      return;
    }
    
    // Transfer today's current clinic state (including all payments, expenses, and withdrawals)
    const transferAmount = Number(currentlyInCachier);
    
    // AUDIT LOG - Create detailed breakdown for debugging (Manual Transfer)
    const auditLog = {
      transferDate,
      transferAmount,
      breakdown: {
        todayPatientPay: Number(todayPatientPay),
        todayExpenses: Number(todayExpenses),
        todayAllInputs: Number(todayAllInputs),
        todayAllWithdraws: Number(todayAllWithdraws),
        calculation: `${todayPatientPay} - ${todayExpenses} + ${todayAllInputs} - ${todayAllWithdraws} = ${transferAmount}`
      },
      timestamp: new Date().toISOString(),
      transferType: 'manual'
    };
    
    console.log('🔍 MANUAL TRANSFER AUDIT LOG:', JSON.stringify(auditLog, null, 2));
    
    // FINAL CHECK: Verify no transfer already exists for this date
    const existingTransfer = withdraws?.find(w => 
      formatDateSafely(w?.date) === transferDate && 
      isCarryOverDescription(w.description)
    );
    
    if (existingTransfer) {
      console.log('⚠️ Manual transfer blocked - already exists for', transferDate, '- Amount:', existingTransfer.amount);
      transferInProgress.current = false;
      return;
    }
    
    const formData = {
      date: transferDate,
      description: CARRY_OVER_DESC,
      amount: transferAmount,
      type: 'input',
      transferId: `manual_${transferDate}_${Date.now()}`, // Unique identifier for manual transfers
      auditLog: JSON.stringify(auditLog), // Store audit trail
      close: () => {}
    }
    
    dispatch(addNewWithdraw(formData)) // Aktivizo transferimin manual
    setAnyTransferCompleted(true) // Deaktivizo çdo lloj transferi tjetër për sot
    
    // PERSIST MANUAL TRANSFER: Mark in localStorage too
    const todayKey = `transfer_completed_${getTodayDate()}`;
    localStorage.setItem(todayKey, 'true');
    
    // Extended lock time to prevent rapid re-execution
    setTimeout(() => {
      transferInProgress.current = false;
    }, 15000); // 15 second delay instead of 5
  }

  // Kontrollo nëse arka është kaluar tashmë për ditën e ardhshme
  const checkIfArkaCarriedOver = () => {
    const nextWorkingDay = getNextWorkingDayTirane(new Date());
    if (!nextWorkingDay) return false;
    
    return withdraws?.some(withdraw => 
      formatDateSafely(withdraw?.date) === nextWorkingDay && 
      isCarryOverDescription(withdraw.description)
    )
  }

  // Automatic daily transfer is handled solely by the Cloud Function (dailyArkaTransfer at 22:00 Europe/Tirane).
  // The frontend only reflects database state and allows manual transfer via the button.

  // Helper to check if selected date is today or yesterday and transfer is allowed
  const isTransferAllowed = () => {
    if (!dateRanges || dateRanges.length !== 1) return false;
    const selected = dateRanges[0];
    const today = getTodayDate();
    const yesterdayDateObj = new Date();
    yesterdayDateObj.setDate(yesterdayDateObj.getDate() - 1);
    const yesterday = formatDateSafely(yesterdayDateObj);
    if (selected === today) return true;
    if (selected === yesterday) {
      // Only allow if there is no transfer for today
      const alreadyTransferred = withdraws?.some((w) =>
        formatDateSafely(w?.date) === today && isCarryOverDescription(w.description)
      );
      return !alreadyTransferred;
    }
    return false;
  };

  return (
    <div className={styles.container}>
      {/* Simple warning with delay to avoid false alarms */}
      {showWarning && (
        <div style={{
          backgroundColor: '#ff6b6b',
          color: 'white',
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '8px',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          ⚠️ KUJDES: Të dhënat e arkës duken se janë fshirë! 
          <br />
          Kontrolloni Firebase Console ose përdorni butonin &quot;Rikthe Arkan&quot; për të rregulluar problemin.
        </div>
      )}
      

      <div className={styles.upperdata_header}>
        <div className={styles.page_date_container}>
          <div className={styles.pt_category_container}>
            <div className={styles.header_buttons}>
              <button
                className={`${styles.header_button} ${selectedType === 'all' ? styles.active : ''}`}
                onClick={() => setSelectedType('all')}
              >
                {t('all')}
              </button>
              <button
                className={`${styles.header_button} ${selectedType === 'stomatology' ? styles.active : ''}`}
                onClick={() => setSelectedType('stomatology')}
              >
                <StomatologyIcon className={styles.stomatology_icon} />
              </button>
              <button
                className={`${styles.header_button} ${selectedType === 'pulmonology' ? styles.active : ''}`}
                onClick={() => setSelectedType('pulmonology')}
              >
                <PulmonologyIcon className={styles.pulmonology_icon} />
              </button>
            </div>
          </div>
          <div className={styles.chart_control_buttons}>
            {profile?.role !== 'standard' && (
              <div className={styles.timespan_control}>
                <button
                  className={`${styles.chart_control_button_timespan} ${dateRangeType === 'day' ? styles.active_timespan : ''}`}
                  onClick={() => switchRange('day')}
                >
                  {t('day')}
                </button>
                <button
                  className={`${styles.chart_control_button_timespan} ${dateRangeType === 'weekly' ? styles.active_timespan : ''}`}
                  onClick={() => switchRange('weekly')}
                >
                  {t('week')}
                </button>
                <button
                  className={`${styles.chart_control_button_timespan} ${dateRangeType === 'monthly' ? styles.active_timespan : ''}`}
                  onClick={() => switchRange('monthly')}
                >
                  {t('month')}
                </button>
                <button
                  className={`${styles.chart_control_button_timespan} ${dateRangeType === 'all' ? styles.active_timespan : ''}`}
                  onClick={() => switchRange('all')}
                >
                  {t('all')}
                </button>
              </div>
            )}
            {profile?.role !== 'standard' && (
              <div className={styles.date_range_control}>
                {dateRangeType === 'day' && (
                  <input
                    type='date'
                    value={dateRangeType === 'day' ? dateRanges[0] : ''}
                    disabled={!(dateRangeType === 'day')}
                    onChange={(e) => {
                      setDateRanges([e.target.value])
                    }}
                  />
                )}
                {dateRangeType === 'monthly' && (
                  <div className={styles.month_year_selectors}>
                    <select
                      value={selectedMonth}
                      onChange={e => setSelectedMonth(e.target.value)}
                      className={styles.month_selector}
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i+1} value={String(i+1).padStart(2, '0')}>
                          {t('month_' + String(i+1).padStart(2, '0'))}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedYear}
                      onChange={e => setSelectedYear(e.target.value)}
                      className={styles.year_selector}
                    >
                      {years.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.income_data}>
          <ValueCard 
            bg='#2BC7BA' 
            title={t('total_profit')} 
            value={'€ ' + displayTotalProfit} 
            icon={<AllIncomeIcon />} 
            lightText={true}
            onClick={() => {}} 
          />
          <ValueCard
            bg='#FFFFFF'
            title={t('total_patient_pay')}
            value={'€ ' + totalPatientPay}
            icon={<ArcIcon />}
            lightText={false}
          />
          <ValueCard
            bg='#FFFFFF'
            title={t('paying_pts')}
            value={payingPatientsSorted?.length}
            icon={<PayingPatients />}
            lightText={false}
            onClick={showPayingPatients}
            style={{ cursor: 'pointer' }}
          />
          <ValueCard
            bg='#FFFFFF'
            title={t('morning_cash_register')}
            value={'€ ' + morningCashRegister}
            icon={<CachierIcon />}
            lightText={false}
          />
          <ValueCard
            bg='#FFFFFF'
            title={t('cachier')}
            onClick={handleAddingNewWithdrawForm}
            value={'€ ' + (dateRanges && dateRanges.length === 1 ? getArkaForDate(dateRanges[0]) : currentlyInCachier)}
            icon={<CachierIcon />}
            lightText={false}
          />
        </div>
      </div>
      <div className={styles.outcome_data}>
        <ValueCard
          bg='#60778E'
          title={t('total_expenses')}
          value={'€ ' + allExpenses}
          icon={<ExpensesIcon />}
          lightText={true}
          hoverableToAdd={true}
          onClick={handleAddingNewExpenseForm}
        />
        <ValueCard bg='#FFFFFF' title={t('food')} value={'€ ' + foodExpenses} icon={<FoodIcon />} lightText={false} />
        <ValueCard bg='#FFFFFF' title={t('material')} value={'€ ' + materialExpenses} icon={<MaterialIcon />} lightText={false} />
        <ValueCard bg='#FFFFFF' title={t('tech')} value={'€ ' + techExpenses} icon={<MaterialIcon />} lightText={false} />
        <ValueCard bg='#FFFFFF' title={t('other')} value={'€ ' + otherExpenses} icon={<ArcDIcon />} lightText={false} />
      </div>
      <div className={styles.view_all_container}>
        {profile?.role !== 'standard' && (
          <>
            <button className={styles.view_all_button} onClick={showArkaChanges}>
              {t('view_cashier_history')}
            </button>
            <button className={styles.view_all_button} onClick={showExpenseChanges}>
              {t('view_expense_history')}
            </button>
            <button type="button" className={styles.view_all_button} onClick={printrecord}>
              {t('print_data')}
            </button>
            {/* Show the transfer button for admins only for today or yesterday (with yesterday logic) */}
            {showArkaButtons && isTransferAllowed() && (
              <button 
                className={`${styles.view_all_button} ${(checkIfArkaCarriedOver() || anyTransferCompleted || transferInProgress.current) ? styles.disabled : ''}`} 
                onClick={() => {
                  // ENHANCED CHECK: Ensure no transfer of any type has been completed today
                  const hasActiveTransfer = checkIfArkaCarriedOver() || anyTransferCompleted || transferInProgress.current;
                  
                  // Check if transfer is already in progress or completed
                  if (hasActiveTransfer) {
                    return;
                  }
                  
                  // TRIPLE LOCK: Final check before execution
                  if (transferInProgress.current || anyTransferCompleted || checkIfArkaCarriedOver()) {
                    return; // Exit if any condition is already true
                  }
                  
                  // IMMEDIATELY lock to prevent multiple clicks
                  transferInProgress.current = true;
                  
                  // Transfer logic: if selected is today, transfer to next working day; if selected is yesterday, transfer to today
                  const selected = dateRanges[0];
                  const today = getTodayDate();
                  let transferDate, transferDesc, transferAmount;
                  if (selected === today) {
                    // If today is Saturday, transfer to Monday
                    transferDate = getNextWorkingDayTirane(new Date());
                    transferDesc = CARRY_OVER_DESC;
                    
                    // Transfer today's current clinic state (including all payments, expenses, and withdrawals)
                    transferAmount = Number(currentlyInCachier);
                  } else {
                    transferDate = today;
                    transferDesc = RETRO_CARRY_OVER_DESC;
                    // Transfer yesterday's balance when doing retroactive transfer
                    transferAmount = Number(getArkaForDate(selected));
                  }
                  if (!transferDate) {
                    transferInProgress.current = false;
                    return;
                  }
                  
                  // ENHANCED DUPLICATE CHECK: Verify no transfer already exists for this date
                  const existingTransfer = withdraws?.find(w => 
                    formatDateSafely(w?.date) === transferDate && 
                    isCarryOverDescription(w.description)
                  );
                  
                  if (existingTransfer) {
                    // Transfer already exists, reset locks and exit
                    console.log('⚠️ Manual transfer blocked - already exists for', transferDate, '- Amount:', existingTransfer.amount);
                    transferInProgress.current = false;
                    setAnyTransferCompleted(true); // Ensure flag is set
                    return;
                  }
                  
                  // PREVENT ZERO TRANSFERS: Don't transfer if amount is zero or negative
                  if (transferAmount <= 0) {
                    console.log('⚠️ Preventing zero/negative manual transfer:', transferAmount);
                    transferInProgress.current = false;
                    return;
                  }
                  
                  const formData = {
                    date: transferDate,
                    description: transferDesc,
                    amount: transferAmount,
                    type: 'input',
                    transferId: `button_${transferDate}_${Date.now()}`, // Unique identifier for button transfers
                    close: () => {}
                  };
                  dispatch(addNewWithdraw(formData));
                  setAnyTransferCompleted(true);
                  
                  // Extended lock time to prevent rapid re-execution
                  setTimeout(() => {
                    transferInProgress.current = false;
                  }, 15000); // 15 second delay instead of 5
                }}
                disabled={checkIfArkaCarriedOver() || anyTransferCompleted || transferInProgress.current || (dateRanges?.[0] === getTodayDate() && Number(currentlyInCachier) < 0)}
                              >
                  {anyTransferCompleted ? 'Transferuar Tashmë' : `Transfero Arken (€${(() => {
                    // For transfer button, show the current balance when viewing today (what will actually be transferred)
                    if (dateRanges && dateRanges.length === 1) {
                      const selected = dateRanges[0];
                      const today = getTodayDate();
                      // If viewing today, show current balance (what will be transferred to tomorrow)
                      if (selected === today) {
                        return currentlyInCachier;
                      }
                      // If viewing yesterday, show what would be transferred from yesterday to today
                      else {
                        return getArkaForDate(selected);
                      }
                    }
                    // Default: show current balance (what will be transferred)
                    return currentlyInCachier;
                  })()})`}
                </button>
            )}
            
            {/* Emergency Arka Reset Button - only for admins when arka is suspicious */}
            {showArkaButtons && Number(currentlyInCachier) < -100 && (
              <button 
                className={styles.view_all_button}
                style={{ backgroundColor: '#ff6b6b', color: 'white' }}
                onClick={() => {
                  const newArka = prompt('🚨 Rivendosje Emergjente e Arkës\n\nVlera aktuale e arkës duket e pasaktë.\nVendosni shumën e saktë të arkës (€):');
                  if (newArka && !isNaN(Number(newArka))) {
                    const todayDate = getTodayDate();
                    const formData = {
                      date: todayDate,
                      description: `Rivendosje Emergjente e Arkës - E mëparshme: €${currentlyInCachier}`,
                      amount: Number(newArka) - Number(currentlyInCachier), // Adjust to reach target
                      type: 'input',
                      close: () => {}
                    };
                    dispatch(addNewWithdraw(formData));
                  }
                }}
                              >
                  🚨 Rivendos Arken (Emergjencë)
                </button>
            )}
          </>
        )}
      </div>
      <div className={styles.bottom_info}>
        {profile?.role !== 'standard' && (
          <div className={styles.chart_container}>
            <ChartControl
              showChartIncome={showChartIncome}
              setShowChartIncome={setShowChartIncome}
              showChartExpenses={showChartExpenses}
              setShowChartExpenses={setShowChartExpenses}
              t={t}
            />
            <div className={styles.chart_wrap}>
              <DualLineCharts
                expenses={expenses}
                historyOfIncome={categorizedApointments
                  ?.filter(apt => apt.payment_date || apt.date) // Përjashto objektet pa datë
                  ?.map(apt => ({
                    date: apt.payment_date || apt.date,
                    amount: apt.patient_pay || apt.paid_amount || 0,
                    patientData: patients?.find(p => p.id === apt.patient_id)?.full_name || 'Unknown Patient'
                  })) || []}
                type={dateRangeType}
                dateRanges={dateRanges}
                showIncome={showChartIncome}
                showExpenses={showChartExpenses}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Export with React.memo for performance optimization
export default React.memo(PaymentsReviewComponent)
